import React, { useState } from 'react';
import { useNavigate, Navigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { DynamicIcon } from '../components/icons/DynamicIcon';
import toast from 'react-hot-toast';

const Login: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isSignUp, setIsSignUp] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const { signIn, signUp, user } = useAuth();
  const navigate = useNavigate();

  // Redirect if already logged in
  if (user) {
    return <Navigate to="/dashboard" replace />;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email || !password) {
      toast.error('Por favor, preencha todos os campos');
      return;
    }

    if (password.length < 6) {
      toast.error('A senha deve ter pelo menos 6 caracteres');
      return;
    }

    setLoading(true);

    try {
      console.log('Tentando login/registro...');
      const { error } = isSignUp 
        ? await signUp(email, password)
        : await signIn(email, password);

      if (error) {
        console.error('Erro retornado:', error);
        toast.error(error);
      } else {
        if (isSignUp) {
          toast.success('Conta criada! Verifique seu email para confirmar.');
        } else {
          toast.success('Login realizado com sucesso!');
          navigate('/dashboard');
        }
      }
    } catch (err) {
      console.error('Erro detalhado:', err);
      toast.error('Algo deu errado. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        {!isSignUp && (
          <div className="text-center mb-8">
            <h1 className="text-4xl font-bold text-gray-900 mb-2">EventPour</h1>
            <p className="text-lg text-gray-600">Orçamentos profissionais em minutos</p>
            <div className="flex justify-center gap-6 mt-6 text-sm text-gray-700">
              <span className="flex items-center gap-1">
                <DynamicIcon name="check" size={16} className="text-green-600" />
                5 orçamentos grátis
              </span>
              <span className="flex items-center gap-1">
                <DynamicIcon name="check" size={16} className="text-green-600" />
                Cálculo automático
              </span>
              <span className="flex items-center gap-1">
                <DynamicIcon name="check" size={16} className="text-green-600" />
                PDF profissional
              </span>
            </div>
          </div>
        )}
        <div className="flex justify-center">
          <DynamicIcon name="glasses" size={48} className="text-blue-600" />
        </div>
        <div className="text-center">
          <div className="mx-auto h-12 w-12 bg-blue-600 rounded-full flex items-center justify-center">
            <DynamicIcon name="user" size={24} className="text-white" />
          </div>
          <h2 className="mt-6 text-3xl font-bold text-gray-900">
            {isSignUp ? 'Criar conta' : 'Entrar na conta'}
          </h2>
          <p className="mt-2 text-sm text-gray-600">
            {isSignUp 
              ? 'Preencha os dados para criar sua conta' 
              : 'Entre com suas credenciais para acessar o sistema'
            }
          </p>
        </div>

        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          <div className="space-y-4">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                Email
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <DynamicIcon name="mail" size={20} className="text-gray-400" />
                </div>
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  placeholder="seu@email.com"
                />
              </div>
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                Senha
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <DynamicIcon name="lock" size={20} className="text-gray-400" />
                </div>
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete={isSignUp ? 'new-password' : 'current-password'}
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="block w-full pl-10 pr-10 py-3 border border-gray-300 rounded-lg placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? (
                    <DynamicIcon name="eyeOff" size={20} className="text-gray-400 hover:text-gray-600" />
                  ) : (
                    <DynamicIcon name="eye" size={20} className="text-gray-400 hover:text-gray-600" />
                  )}
                </button>
              </div>
              {isSignUp && (
                <p className="mt-1 text-xs text-gray-500">
                  Mínimo de 6 caracteres
                </p>
              )}
            </div>
          </div>

          <div>
            <button
              type="submit"
              disabled={loading}
              className="group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-lg text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {loading ? (
                <div className="flex items-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  {isSignUp ? 'Criando conta...' : 'Entrando...'}
                </div>
              ) : (
                <div className="flex items-center">
                  <DynamicIcon name={isSignUp ? 'user' : 'user'} size={16} className="mr-2" />
                  {isSignUp ? 'Criar conta' : 'Entrar'}
                </div>
              )}
            </button>
          </div>

          <div className="text-center">
            <button
              type="button"
              onClick={() => setIsSignUp(!isSignUp)}
              className="text-blue-600 hover:text-blue-500 text-sm font-medium transition-colors"
            >
              {isSignUp 
                ? 'Já tem uma conta? Entre aqui' 
                : 'Não tem uma conta? Cadastre-se'
              }
            </button>
          </div>
        </form>

        <p className="text-center text-sm text-gray-600 mt-6">
          Não tem uma conta?{' '}
          <Link to="/signup" className="text-purple-600 hover:text-purple-700 font-medium">
            Criar conta grátis
          </Link>
        </p>
      </div>
    </div>
  );
};

export default Login;